import React from 'react';
import { InputAdornment, TextField } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

export interface NumberFieldProps {
  value: string;
  onChange: (value: string) => void;
  min?: number;
  max?: number;
  step?: number | string;
  /** Whole numbers only: no decimal point, numeric keypad. */
  integer?: boolean;
  /** '$' sits at the start; '%' or any other text (e.g. 'units') at the end. */
  adornment?: '$' | '%' | string;
  /** When false an empty box is an error. Default true. */
  allowEmpty?: boolean;
  label?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  error?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
  'aria-label'?: string;
}

/** '' or anything that is not a finite number gives null. */
export function parseNumberInput(value: string | null | undefined): number | null {
  const v = String(value ?? '').trim();
  if (!v || v === '.' || v === '-') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const rangeMessage = (min?: number, max?: number): string => {
  if (min !== undefined && max !== undefined) return `Enter a value from ${min} to ${max}`;
  if (min !== undefined) return `Enter a value of ${min} or more`;
  return `Enter a value of ${max} or less`;
};

/**
 * Controlled string-in, string-out number box. Keeps the call site's state shape
 * while blocking exponent/sign keys, ignoring wheel scroll and clamping on blur.
 */
export const NumberField: React.FC<NumberFieldProps> = ({
  value,
  onChange,
  min,
  max,
  step,
  integer = false,
  adornment,
  allowEmpty = true,
  label,
  placeholder,
  helperText,
  error,
  disabled,
  size = 'small',
  sx,
  'aria-label': ariaLabel,
}) => {
  const allowNegative = min === undefined || min < 0;
  const parsed = parseNumberInput(value);
  const empty = !String(value ?? '').trim();

  let rangeError: string | null = null;
  if (empty) {
    if (!allowEmpty) rangeError = 'This field is required';
  } else if (parsed === null) {
    rangeError = 'Enter a number';
  } else if (integer && !Number.isInteger(parsed)) {
    rangeError = 'Enter a whole number';
  } else if ((min !== undefined && parsed < min) || (max !== undefined && parsed > max)) {
    rangeError = rangeMessage(min, max);
  }

  const blocked = new Set(['e', 'E', '+']);
  if (!allowNegative) blocked.add('-');
  if (integer) blocked.add('.');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    const pattern = integer
      ? allowNegative ? /^-?\d*$/ : /^\d*$/
      : allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
    if (pattern.test(next)) onChange(next);
  };

  const handleBlur = () => {
    if (parsed === null) {
      if (!empty) onChange('');
      return;
    }
    let next = parsed;
    if (min !== undefined && next < min) next = min;
    if (max !== undefined && next > max) next = max;
    if (integer) next = Math.round(next);
    if (String(next) !== value) onChange(String(next));
  };

  const adornmentNode = adornment ? <InputAdornment position={adornment === '$' ? 'start' : 'end'}>{adornment}</InputAdornment> : undefined;
  const shownError = Boolean(error) || Boolean(rangeError);

  return (
    <TextField
      type="text"
      size={size}
      label={label}
      placeholder={placeholder}
      value={value}
      disabled={disabled}
      error={shownError}
      helperText={rangeError ?? helperText}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={(e) => {
        if (blocked.has(e.key)) e.preventDefault();
      }}
      onWheel={(e) => (e.target as HTMLElement).blur?.()}
      sx={sx}
      slotProps={{
        htmlInput: {
          inputMode: integer ? 'numeric' : 'decimal',
          min,
          max,
          step,
          'aria-label': ariaLabel ?? label,
        },
        input: adornmentNode
          ? adornment === '$'
            ? { startAdornment: adornmentNode }
            : { endAdornment: adornmentNode }
          : undefined,
      }}
    />
  );
};
